import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { get } from '../utils/helpers/api';
import { useAuth } from './auth';

const BranchContext = createContext();

const readStored = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const BranchProvider = ({ children }) => {
  const { isLoggedIn, can } = useAuth();

  const [branches, setBranches] = useState(() => readStored('branches', []));
  const [warehouses, setWarehouses] = useState([]);
  const [activeBranchId, setActiveBranchId] = useState(() => localStorage.getItem('active_branch_id'));
  // No setter: switching scope writes localStorage and reloads, so this is only
  // ever read at mount.
  const [allBranchesMode] = useState(() => localStorage.getItem('branch_scope') === 'all');
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);

    const response = await get('/branches/accessible');

    if (response?.success) {
      setBranches(response.branches ?? []);
      setWarehouses(response.warehouses ?? []);
      localStorage.setItem('branches', JSON.stringify(response.branches ?? []));

      // The server is the authority on which branch is active: it re-validates
      // the header against the user's access on every request.
      if (response.active_branch_id && !allBranchesMode) {
        setActiveBranchId(response.active_branch_id);
        localStorage.setItem('active_branch_id', response.active_branch_id);
      }
    }

    setLoading(false);
  }, [allBranchesMode]);

  useEffect(() => {
    if (isLoggedIn) {
      refresh();
    }
  }, [isLoggedIn, refresh]);

  /**
   * Switching branch reloads the page.
   *
   * Around sixty screens hold fetched rows, form drafts, previewed document
   * numbers and grid state; a soft refetch would need every one of them to
   * subscribe to this context, and anything missed would show one branch's data
   * under another's name. The language switcher already sets this precedent.
   */
  const switchBranch = useCallback((branchId) => {
    if (branchId === 'all') {
      localStorage.setItem('branch_scope', 'all');
    } else {
      localStorage.removeItem('branch_scope');
      localStorage.setItem('active_branch_id', branchId);
    }

    // A record open in one branch will not exist in the next, so detail and
    // form routes go back to their list instead of reloading into a 404.
    const path = window.location.pathname;
    const isRecordScoped = /\/(view|edit|create)(\/|$)/.test(path);

    if (isRecordScoped) {
      const listPath = path.replace(/\/(view|edit|create)(\/.*)?$/, '');
      window.location.href = listPath || '/home';
      return;
    }

    window.location.reload();
  }, []);

  const value = useMemo(() => {
    const activeBranch = branches.find((branch) => branch.id === activeBranchId) ?? null;

    return {
      branches,
      warehouses,
      activeBranchId,
      activeBranch,
      allBranchesMode,
      loading,
      // Single-branch companies should see no trace of the feature.
      isMultiBranch: branches.length > 1,
      canViewAll: typeof can === 'function' && can('settings.branches.view_all'),
      branchWarehouses: warehouses.filter((warehouse) => warehouse.branch_id === activeBranchId),
      warehousesForBranch: (branchId) => warehouses.filter((warehouse) => warehouse.branch_id === branchId),
      switchBranch,
      refresh,
    };
  }, [branches, warehouses, activeBranchId, allBranchesMode, loading, can, switchBranch, refresh]);

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
};

export const useBranch = () => useContext(BranchContext);
